/**
 * Mappa posizione → sezione del manuale. Le sezioni dichiarano pattern di rotta di React Router,
 * con un frammento opzionale (`/admin/webinar/:id#test`) che corrisponde alla scheda aperta
 * (parametro `?scheda=` delle pagine a schede) o all'hash dell'URL.
 */
import type { Ruolo } from '@aulaviva/shared';
import { matchPath } from 'react-router';
import { SEZIONE_PREDEFINITA, SEZIONI, type AreaManuale, type SezioneIndicizzata } from './indice';

/** Le aree del manuale visibili a un ruolo: l'amministratore le vede tutte. */
export function areeVisibili(ruolo: Ruolo | null): AreaManuale[] {
  if (ruolo === 'admin') return ['tutti', 'partecipante', 'relatore', 'regia', 'admin'];
  if (ruolo === 'partecipante' || ruolo === 'relatore' || ruolo === 'regia') return ['tutti', ruolo];
  return ['tutti'];
}

export function sezioniPerRuolo(ruolo: Ruolo | null, sezioni: SezioneIndicizzata[] = SEZIONI): SezioneIndicizzata[] {
  const aree = areeVisibili(ruolo);
  return sezioni.filter((s) => aree.includes(s.area));
}

function punteggio(pattern: string, pathname: string, frammento: string | null): number {
  if (pattern === '*') return 1;
  const [percorso, frammentoPattern] = pattern.split('#') as [string, string | undefined];
  if (!matchPath({ path: percorso, end: true }, pathname)) return 0;
  if (frammentoPattern) {
    if (frammentoPattern !== frammento) return 0;
    return 1000 + specificita(percorso);
  }
  return 100 + specificita(percorso);
}

/** Più segmenti statici, più specifico. */
function specificita(percorso: string): number {
  const segmenti = percorso.split('/').filter(Boolean);
  return segmenti.filter((s) => !s.startsWith(':') && s !== '*').length * 10 + segmenti.length;
}

/**
 * Sezione da aprire per la posizione corrente. `frammento` è la scheda aperta (senza `#`).
 * Senza corrispondenza si apre l'introduzione.
 */
export function sezionePerPosizione(pathname: string, frammento: string | null, ruolo: Ruolo | null, sezioni: SezioneIndicizzata[] = SEZIONI): SezioneIndicizzata {
  const candidate = sezioniPerRuolo(ruolo, sezioni);
  let migliore: { sezione: SezioneIndicizzata; punti: number } | null = null;
  for (const sezione of candidate) {
    for (const pattern of sezione.rotte) {
      const punti = punteggio(pattern, pathname, frammento);
      if (punti > 0 && (!migliore || punti > migliore.punti)) migliore = { sezione, punti };
    }
  }
  return migliore?.sezione ?? candidate.find((s) => s.id === SEZIONE_PREDEFINITA) ?? candidate[0]!;
}

/** Frammento dalla posizione del browser: `?scheda=` delle pagine a schede, altrimenti l'hash. */
export function frammentoDa(search: string, hash: string): string | null {
  const scheda = new URLSearchParams(search).get('scheda');
  if (scheda) return scheda;
  return hash ? hash.replace(/^#/, '') : null;
}
